import { useTranslation } from 'react-i18next';
import { RelatedDocument } from '@asset-metrix/portal-gateway-client-js';
import clsx from 'clsx';

import { getSVGIconNameFromDocument } from '../../utils/getSVGIconNameFromDocument';
import { SVGIcon } from '../icons/SVGIcon';
import { TextWithTooltip } from '../tooltip/TextWithTooltip';

import styles from './document-listing.module.scss';

type DocumentListingProps = {
  appearance?: 'default' | 'panel';
  className?: string;
  documents?: RelatedDocument[];
  moreEntriesCount?: number;
  onDocumentClick?: (document: RelatedDocument) => void;
  selectedDocument?: RelatedDocument;
};

export function DocumentListing({
  appearance = 'default',
  className,
  documents,
  moreEntriesCount,
  onDocumentClick,
  selectedDocument,
}: DocumentListingProps) {
  const { t } = useTranslation();

  const renderDocuments = () => {
    if (!documents || documents.length === 0) {
      return (
        <div
          className={styles['no-documents-available']}
          data-testid="no-documents-available"
        >
          {t('app.pageSidebar.noDocumentsAvailable')}
        </div>
      );
    }

    return (
      <>
        {documents.map((document) => {
          const { id, fileName, mimetype, virusFree } = document;

          return (
            <div
              className={clsx(styles['row'], {
                [styles['row--selected']]: id === selectedDocument?.id,
              })}
              onClick={() => onDocumentClick?.(document)}
              key={id}
              data-testid={`document-row-${id}`}
            >
              {virusFree === false ? (
                <TextWithTooltip
                  tooltipProps={{
                    disabled: false,
                    label: t('app.table.tooltip.maliciousContent'),
                  }}
                >
                  <SVGIcon name="file-alert" />
                </TextWithTooltip>
              ) : (
                <SVGIcon
                  name={getSVGIconNameFromDocument({
                    fileName,
                    mimetype,
                  })}
                />
              )}
              <TextWithTooltip
                textProps={{
                  className: styles['file-name'],
                  lineClamp: 1,
                }}
                tooltipProps={{ usePortal: false }}
              >
                {fileName}
              </TextWithTooltip>
            </div>
          );
        })}
        {moreEntriesCount != null && moreEntriesCount > 0 && (
          <div className={styles['more-entries']} data-testid="more-entries">
            {t('app.table.showMoreEntry', '', { count: moreEntriesCount })}
          </div>
        )}
      </>
    );
  };

  return (
    <div
      data-testid="document-listing"
      className={clsx(styles['root'], className, {
        [styles['root--panel']]: appearance === 'panel',
      })}
    >
      {renderDocuments()}
    </div>
  );
}
